/**
 * ApprovalProcessDetail Component
 *
 * Displays detailed view of an approval process including:
 * - Header with process name, status, and actions
 * - Process information card
 * - Entry criteria card
 * - Visual approval steps flow
 * - Actions configuration card
 * - Statistics card
 * - Recent requests table
 *
 * @module components/admin/approval-processes/ApprovalProcessDetail
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft,
  Edit2,
  Copy,
  Trash2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Users,
  User,
  Shield,
  Inbox,
  UserCheck,
  FileText,
  Mail,
  ListChecks,
  Webhook,
  Database,
  Calendar,
  TrendingUp,
  Activity,
  MoreVertical
} from 'lucide-react';
import { StandardButton } from '../../standard/StandardButton';
import { StandardModal } from '../../standard/StandardModal';
import { StandardCard } from '../../standard/StandardCard';
import { StandardLoadingState } from '../../standard/StandardLoadingState';
import { StandardEmptyState } from '../../standard/StandardEmptyState';
import { useToast } from '../../standard/StandardToast';
import {
  ApprovalProcess,
  ApprovalRequest,
  ApprovalStep,
  ApprovalCriteria,
  ApprovalProcessAction,
  ProcessStats
} from '../../../types/approvalProcesses';
import * as approvalProcessStorageRouter from '../../../services/approvalProcessStorageRouter';
import { approvalProcessStorage } from '../../../services/approvalProcessStorage';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';

interface ApprovalProcessDetailProps {
  processId: string;
  onBack: () => void;
  onEdit: (processId: string) => void;
}

// Operator display labels
const operatorLabels: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  greater_than: 'is greater than',
  less_than: 'is less than',
  greater_or_equal: 'is greater than or equal to',
  less_or_equal: 'is less than or equal to',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  in_list: 'is in',
  not_in_list: 'is not in',
  is_empty: 'is empty',
  is_not_empty: 'is not empty'
};

// Approver type icons and labels
const approverTypeConfig: Record<string, { icon: React.ElementType; label: string }> = {
  user: { icon: User, label: 'Specific User' },
  role: { icon: Shield, label: 'Role' },
  manager: { icon: UserCheck, label: 'Manager' },
  queue: { icon: Inbox, label: 'Queue' },
  related_user: { icon: Users, label: 'Related User' }
};

// Action type icons
const actionTypeIcons: Record<string, React.ElementType> = {
  field_update: Database,
  email_alert: Mail,
  task_creation: ListChecks,
  webhook: Webhook
};

export const ApprovalProcessDetail: React.FC<ApprovalProcessDetailProps> = ({
  processId,
  onBack,
  onEdit
}) => {
  const toast = useToast();
  const [process, setProcess] = useState<ApprovalProcess | null>(null);
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [stats, setStats] = useState<ProcessStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Load process data
  useEffect(() => {
    loadProcessData();
  }, [processId]);

  const loadProcessData = async () => {
    setLoading(true);
    try {
      const processData = await approvalProcessStorageRouter.getProcessAsync(processId);
      if (processData) {
        setProcess(processData);
        const processStats = await approvalProcessStorageRouter.getProcessStatsAsync(processId);
        setStats(processStats);
        const processRequests = USE_GOBODO_API
          ? []
          : approvalProcessStorage
              .getAllRequests()
              .filter(r => r.processId === processId)
              .sort((a, b) => new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime())
              .slice(0, 10);
        setRequests(processRequests);
      }
    } catch (error) {
      toast.error('Error', 'Failed to load approval process');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!process) return;
    try {
      const updated = await approvalProcessStorageRouter.toggleProcessStatusAsync(processId);
      if (updated) {
        setProcess(updated);
        toast.success('Success', `Process ${process.isActive ? 'deactivated' : 'activated'} successfully`);
      }
    } catch (error) {
      toast.error('Error', 'Failed to update process status');
    }
  };

  const handleClone = async () => {
    if (!process) return;
    try {
      const cloned = await approvalProcessStorageRouter.cloneProcessAsync(
        processId,
        `${process.name} (Copy)`
      );
      if (cloned) {
        toast.success('Success', `Process cloned as "${cloned.name}"`);
      }
      setShowActionsMenu(false);
    } catch (error) {
      toast.error('Error', 'Failed to clone process');
    }
  };

  const handleDelete = async () => {
    try {
      await approvalProcessStorageRouter.deleteProcessAsync(processId);
      toast.success('Success', 'Process deleted successfully');
      onBack();
    } catch (error) {
      toast.error('Error', 'Failed to delete process');
    }
  };

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      recalled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      escalated: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    };
    return styles[status] || styles.pending;
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    const icons: Record<string, React.ElementType> = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
      recalled: ArrowLeft,
      escalated: AlertTriangle
    };
    return icons[status] || Clock;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Render criteria display
  const renderCriteria = (criteria: ApprovalCriteria[], logic: 'AND' | 'OR') => {
    if (criteria.length === 0) {
      return <p className="text-gray-500 dark:text-gray-400 italic">No entry criteria defined - process will apply to all records</p>;
    }

    return (
      <div className="space-y-2">
        {criteria.map((c, index) => (
          <div key={c.id} className="flex items-center gap-2 text-sm">
            <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded font-mono text-blue-600 dark:text-blue-400">
              {c.field}
            </code>
            <span className="text-gray-600 dark:text-gray-400">{operatorLabels[c.operator] || c.operator}</span>
            {c.operator !== 'is_empty' && c.operator !== 'is_not_empty' && (
              <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded font-mono text-green-600 dark:text-green-400">
                {Array.isArray(c.value) ? c.value.join(', ') : String(c.value)}
              </code>
            )}
            {index < criteria.length - 1 && (
              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-xs font-medium">
                {logic}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render step card
  const renderStepCard = (step: ApprovalStep, index: number, totalSteps: number) => {
    const ApproverIcon = approverTypeConfig[step.approverType]?.icon || User;

    return (
      <div key={step.id} className="flex items-start gap-4">
        {/* Step circle and connector */}
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-lg">
            {step.stepNumber}
          </div>
          {index < totalSteps - 1 && (
            <div className="w-0.5 h-16 bg-gray-300 dark:bg-gray-600 my-2" />
          )}
        </div>

        {/* Step content */}
        <div className="flex-1 pb-6">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">{step.name}</h4>
                {step.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{step.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {step.unanimousApproval && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-xs">
                    Unanimous
                  </span>
                )}
                {step.allowDelegation && (
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">
                    Delegation Allowed
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <ApproverIcon className="w-4 h-4" />
              <span>{approverTypeConfig[step.approverType]?.label || step.approverType}</span>
              {step.approverId && (
                <>
                  <span>•</span>
                  <span className="font-medium text-gray-900 dark:text-white">{step.approverId}</span>
                </>
              )}
              {step.approverField && (
                <>
                  <span>•</span>
                  <code className="px-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">{step.approverField}</code>
                </>
              )}
            </div>

            {step.autoEscalateAfterDays && (
              <div className="mt-2 flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <AlertTriangle className="w-4 h-4" />
                <span>Auto-escalate after {step.autoEscalateAfterDays} days</span>
              </div>
            )}

            {step.rejectionReasons && step.rejectionReasons.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Rejection Reasons:</p>
                <div className="flex flex-wrap gap-1">
                  {step.rejectionReasons.map((reason, i) => (
                    <span key={i} className="px-2 py-0.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded text-xs">
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render action item
  const renderActionItem = (action: ApprovalProcessAction) => {
    const ActionIcon = actionTypeIcons[action.type] || FileText;

    return (
      <div key={action.id} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
        <ActionIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{action.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{action.type.replace(/_/g, ' ')}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return <StandardLoadingState message="Loading approval process..." />;
  }

  if (!process) {
    return (
      <StandardEmptyState
        icon={<FileText size={48} />}
        title="Process Not Found"
        message="The approval process you're looking for doesn't exist or has been deleted."
        actionLabel="Back to List"
        onAction={onBack}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{process.name}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  process.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {process.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-full text-sm font-medium">
                  {process.objectType}
                </span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{process.description}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500 dark:text-gray-400">
                <span>Order: {process.order}</span>
                <span>•</span>
                <span>{process.steps.length} step{process.steps.length !== 1 ? 's' : ''}</span>
                <span>•</span>
                <span>Created {formatRelativeTime(process.createdDate)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <StandardButton
              variant={process.isActive ? 'secondary' : 'primary'}
              size="sm"
              onClick={handleToggleStatus}
            >
              {process.isActive ? (
                <>
                  <ToggleRight className="w-4 h-4 mr-1" />
                  Deactivate
                </>
              ) : (
                <>
                  <ToggleLeft className="w-4 h-4 mr-1" />
                  Activate
                </>
              )}
            </StandardButton>

            <StandardButton
              variant="secondary"
              size="sm"
              onClick={() => onEdit(processId)}
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </StandardButton>

            <div className="relative">
              <StandardButton
                variant="ghost"
                size="sm"
                onClick={() => setShowActionsMenu(!showActionsMenu)}
              >
                <MoreVertical className="w-4 h-4" />
              </StandardButton>

              {showActionsMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowActionsMenu(false)}
                  />
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                    <button
                      onClick={handleClone}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Clone Process
                    </button>
                    <button
                      onClick={() => {
                        setShowActionsMenu(false);
                        setShowDeleteModal(true);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Process
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Requests</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSubmitted}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Approval Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.approvalRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Avg. Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avgCompletionTime.toFixed(1)}h</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Process Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Entry Criteria Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Entry Criteria
            </h2>
            {renderCriteria(process.entryCriteria, process.entryCriteriaLogic)}
          </div>

          {/* Approval Steps Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Approval Steps
            </h2>

            {process.steps.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic">No approval steps defined</p>
            ) : (
              <div className="pl-2">
                {process.steps
                  .sort((a, b) => a.stepNumber - b.stepNumber)
                  .map((step, index) => renderStepCard(step, index, process.steps.length))}
              </div>
            )}
          </div>

          {/* Recent Requests Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Recent Requests
            </h2>

            {requests.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic text-center py-8">
                No requests submitted yet
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Record</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Submitted By</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Step</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(request => {
                      const StatusIcon = getStatusIcon(request.status);
                      return (
                        <tr key={request.id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-900 dark:text-white">{request.recordName}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{request.objectType}</p>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {request.submittedByName}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {formatRelativeTime(request.submittedDate)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                              <StatusIcon className="w-3 h-3" />
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {request.status === 'pending' ? (
                              `${request.currentStepNumber}/${request.totalSteps}`
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Settings & Actions */}
        <div className="space-y-6">
          {/* Process Settings Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Settings
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Allow Recall</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  process.allowRecall
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {process.allowRecall ? 'Yes' : 'No'}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Record Editability</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded text-xs font-medium capitalize">
                  {process.recordEditability.replace(/_/g, ' ')}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Approver Can Edit</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  process.approverCanEdit
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {process.approverCanEdit ? 'Yes' : 'No'}
                </span>
              </div>

              <div className="pt-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Email Templates</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Approval:</span>
                    <span className="text-gray-900 dark:text-white">
                      {process.approvalEmailTemplateId || 'Default'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Rejection:</span>
                    <span className="text-gray-900 dark:text-white">
                      {process.rejectionEmailTemplateId || 'Default'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Cards */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Automated Actions
            </h2>

            <div className="space-y-4">
              {/* Initial Submission Actions */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">On Submission</h3>
                {process.initialSubmissionActions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">No actions</p>
                ) : (
                  <div className="space-y-2">
                    {process.initialSubmissionActions.map(renderActionItem)}
                  </div>
                )}
              </div>

              {/* Final Approval Actions */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">On Approval</h3>
                {process.finalApprovalActions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">No actions</p>
                ) : (
                  <div className="space-y-2">
                    {process.finalApprovalActions.map(renderActionItem)}
                  </div>
                )}
              </div>

              {/* Final Rejection Actions */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">On Rejection</h3>
                {process.finalRejectionActions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">No actions</p>
                ) : (
                  <div className="space-y-2">
                    {process.finalRejectionActions.map(renderActionItem)}
                  </div>
                )}
              </div>

              {/* Recall Actions */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">On Recall</h3>
                {process.recallActions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">No actions</p>
                ) : (
                  <div className="space-y-2">
                    {process.recallActions.map(renderActionItem)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Metadata Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Metadata
            </h2>

            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Created</p>
                <p className="text-gray-900 dark:text-white">{formatDate(process.createdDate)}</p>
                <p className="text-gray-500 dark:text-gray-400">by {process.createdBy}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Last Modified</p>
                <p className="text-gray-900 dark:text-white">{formatDate(process.lastModifiedDate)}</p>
                <p className="text-gray-500 dark:text-gray-400">by {process.lastModifiedBy}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Approval Process"
        size="md"
      >
        <StandardModal.Body>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                Are you sure you want to delete "{process.name}"?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone. All associated approval requests will also be deleted.
              </p>
            </div>
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="danger"
            onClick={handleDelete}
            leftIcon={<Trash2 size={16} />}
          >
            Delete Process
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>
    </div>
  );
};
